// Shared building blocks used by Hydrant / Sprinkler / Tank / Detection / Ventilation deep-dive pages.
import React from "react";
import { Badge } from "../ui/badge";

export function StatusDotLg({ status }) {
  const s = (status || "").toLowerCase();
  const isRun = ["running", "working", "online", "active", "open"].includes(s);
  const isFault = ["fault", "alarm"].includes(s);
  const cls = isRun
    ? "from-emerald-400 to-emerald-600 shadow-emerald-500/40 status-pulse-green"
    : isFault
    ? "from-yellow-400 to-yellow-600 shadow-yellow-500/40"
    : "from-red-500 to-red-700 shadow-red-500/40";
  return <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${cls} shadow-lg flex items-center justify-center`}><div className="w-2.5 h-2.5 rounded-full bg-white/90" /></div>;
}

export function MiniDot({ on, label }) {
  return (
    <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
      <span className="text-xs text-gray-600">{label}</span>
      <span className={`px-2 py-0.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-red-50 border border-red-200 text-red-700"}`}>{on ? "ON" : "OFF"}</span>
    </div>
  );
}

export function ArcGauge({ value, label, sublabel }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const radius = 56, circumference = Math.PI * radius;
  const dash = (pct / 100) * circumference;
  const color = pct < 25 ? "#ef4444" : pct < 50 ? "#f59e0b" : "#22c55e";
  return (
    <div className="relative w-32 h-20">
      <svg viewBox="0 0 140 80" className="w-full">
        <path d="M 14 76 A 56 56 0 0 1 126 76" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" strokeLinecap="round" />
        <path d="M 14 76 A 56 56 0 0 1 126 76" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${dash} ${circumference}`} style={{ transition: "stroke-dasharray 0.6s, stroke 0.4s" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-end">
        <div className="text-2xl font-extrabold text-gray-900 leading-none">{pct.toFixed(0)}<span className="text-xs text-gray-500">%</span></div>
        {label && <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>}
        {sublabel && <div className="text-[10px] text-gray-500">{sublabel}</div>}
      </div>
    </div>
  );
}

export function TankColumn({ value, capacityLitres }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const TANK_W = 80, TANK_H = 160;
  const fillH = Math.max(4, (pct / 100) * TANK_H);
  // <30% red, <50% orange, ≥50% green
  const color = pct < 30 ? "#EF4444" : pct < 50 ? "#F97316" : "#16A34A";
  const litres = capacityLitres ? Math.round(capacityLitres * pct / 100) : null;
  // Stable ID — regenerating on every render would break the clipPath reference
  const [clipId] = React.useState(() => `tankClip_${Math.random().toString(36).slice(2, 8)}`);
  // scaleY from the bottom: transform-origin at bottom-centre of the tank rect
  const scaleY = fillH / TANK_H;
  const originX = 10 + TANK_W / 2;
  const originY = 10 + TANK_H;
  return (
    <div className="flex items-center gap-4">
      <svg width={TANK_W + 20} height={TANK_H + 20} viewBox={`0 0 ${TANK_W + 20} ${TANK_H + 20}`}>
        <defs>
          <clipPath id={clipId}>
            <rect x="10" y="10" width={TANK_W} height={TANK_H} rx="8" ry="8" />
          </clipPath>
        </defs>
        {/* Tank shell */}
        <rect x="10" y="10" width={TANK_W} height={TANK_H} rx="8" ry="8" fill="#1F2937" stroke="#374151" strokeWidth="2" />
        {/* Fill — scaleY anchored at bottom so level rises/falls smoothly */}
        <rect
          x="10" y="10" width={TANK_W} height={TANK_H}
          fill={color}
          clipPath={`url(#${clipId})`}
          style={{
            transformOrigin: `${originX}px ${originY}px`,
            transform: `scaleY(${scaleY})`,
            transition: "transform 0.8s cubic-bezier(0.4, 0, 0.2, 1), fill 0.5s ease",
          }}
        >
          <animate attributeName="opacity" values="0.8;1;0.8" dur="2.5s" repeatCount="indefinite" />
        </rect>
        {/* Level tick marks */}
        {[25, 50, 75].map((m) => (
          <line key={m} x1="10" x2="18" y1={10 + TANK_H - (m / 100) * TANK_H} y2={10 + TANK_H - (m / 100) * TANK_H} stroke="#6B7280" strokeWidth="1.5" />
        ))}
        <text x={10 + TANK_W / 2} y={10 + TANK_H / 2 + 6} textAnchor="middle" fill="white" fontWeight="bold" fontSize="18">{pct.toFixed(0)}%</text>
      </svg>
      <div>
        <div className="text-3xl font-extrabold text-gray-900">{pct.toFixed(0)}<span className="text-base text-gray-500">%</span></div>
        {litres != null && <div className="text-xs text-gray-500 mt-1">{litres.toLocaleString("en-IN")} L</div>}
        {capacityLitres && <div className="text-[10px] text-gray-500">capacity {capacityLitres.toLocaleString("en-IN")} L</div>}
      </div>
    </div>
  );
}

/**
 * Unified parameter status evaluator.
 * Returns {label, tone, ok} — used for badges on all ACMS deep-dive pages.
 * direction: "min" → value must be >= threshold (e.g. hydrant pressure 3.5 Bar)
 *            "max" → value must be <= threshold (e.g. jockey cycles/hr ≤ 6)
 * warnBand: fraction of threshold to consider WARNING zone (default 0.15 = within 15%)
 */
export function getParameterStatus(value, threshold, direction = "min", warnBand = 0.15) {
  if (value == null || isNaN(value)) return { label: "UNKNOWN", tone: "slate", ok: false };
  if (direction === "min") {
    if (value >= threshold) return { label: "COMPLIANT", tone: "emerald", ok: true };
    if (value >= threshold * (1 - warnBand)) return { label: "WARNING", tone: "amber", ok: false };
    return { label: "CRITICAL", tone: "red", ok: false };
  }
  // direction === "max"
  if (value <= threshold) return { label: "COMPLIANT", tone: "emerald", ok: true };
  if (value <= threshold * (1 + warnBand)) return { label: "WARNING", tone: "amber", ok: false };
  return { label: "CRITICAL", tone: "red", ok: false };
}

export function StatusBadge({ status }) {
  const tones = {
    emerald: "bg-emerald-50 border border-emerald-200 text-emerald-700",
    amber: "bg-amber-50 border border-amber-200 text-amber-700",
    red: "bg-red-50 border border-red-200 text-red-700 status-pulse-red",
    slate: "bg-slate-100 border border-slate-200 text-slate-700",
  };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${tones[status.tone] || tones.slate}`}>{status.label}</span>;
}

/**
 * Compliance row table for GR breakdown.
 * rows = [{ parameter, current, required, ok }]
 */
export function ComplianceTable({ title, rows }) {
  // ok === true → COMPLIANT, ok === false → CRITICAL, ok === null → UNKNOWN (no live data)
  const knownRows = rows.filter((r) => r.ok !== null && r.ok !== undefined);
  const allOk = knownRows.length > 0 && knownRows.every((r) => r.ok === true);
  const hasFailure = knownRows.some((r) => r.ok === false);
  const headerClass = allOk ? "bg-emerald-500/10" : hasFailure ? "bg-red-500/10" : "bg-slate-500/10";
  const overallLabel = allOk ? "COMPLIANT" : hasFailure ? "NON-COMPLIANT" : "NO DATA";
  const overallBadgeClass = allOk
    ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
    : hasFailure
    ? "bg-red-50 border border-red-200 text-red-700"
    : "bg-slate-100 border border-slate-200 text-slate-600";
  return (
    <div className="glass rounded-lg overflow-hidden">
      <div className={`px-5 py-3 flex items-center justify-between ${headerClass}`}>
        <div className="text-sm uppercase tracking-wider font-bold">{title}</div>
        <Badge className={`border-0 ${overallBadgeClass}`}>{overallLabel}</Badge>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
            <th className="px-5 py-2 font-semibold">Parameter</th>
            <th className="px-5 py-2 font-semibold">Current Value</th>
            <th className="px-5 py-2 font-semibold">GR Requirement</th>
            <th className="px-5 py-2 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const isUnknown = r.ok === null || r.ok === undefined;
            const badgeClass = isUnknown
              ? "bg-slate-100 border border-slate-200 text-slate-600"
              : r.ok
              ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
              : "bg-red-50 border border-red-200 text-red-700 status-pulse-red";
            const badgeLabel = isUnknown ? "UNKNOWN" : r.ok ? "OK" : "CRITICAL";
            return (
              <tr key={i} className={`border-t border-gray-200 ${i % 2 ? "bg-white/[0.015]" : ""}`}>
                <td className="px-5 py-2.5 font-medium">{r.parameter}</td>
                <td className="px-5 py-2.5 font-mono text-orange-300">{r.current}</td>
                <td className="px-5 py-2.5 font-mono text-gray-500">{r.required}</td>
                <td className="px-5 py-2.5"><Badge className={`border-0 ${badgeClass}`}>{badgeLabel}</Badge></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
